/**
 * Blog Article Enhancements
 * 
 * Provides enhanced functionality for blog articles:
 * - Product card generation from bracketed URLs
 * - Blog card generation from bracketed URLs
 * - Table of contents generation from H2-H4 headings (nested)
 */

(() => {
  const origin = document.querySelector('.blog-post-content');
  if (!origin) return;

  /**
   * Replace paragraph URLs with product/blog cards
   */
  {
    const regex = /\[(https:\/\/.*)\]/;
    const domParser = new DOMParser();

    const targets = [...origin.querySelectorAll('p')].filter((el) =>
      regex.test(el.textContent)
    );

    const replaceParagraphToProductLink = async (snippet) => {
      const match = snippet.textContent.match(regex);
      if (!match) return;

      const [, url] = match;
      if (!url) return;

      // Handle blog URLs
      if (url.includes('blogs')) {
        try {
          const response = await fetch(url);
          const rawHTML = await response.text();
          const parsed = domParser.parseFromString(rawHTML, 'text/html');

          const titleElement = parsed.querySelector('meta[property="og:title"]');
          const title = titleElement && titleElement.getAttribute('content');

          const descriptionElement = parsed.querySelector(
            'meta[property="og:description"]'
          );
          const description =
            descriptionElement && descriptionElement.getAttribute('content');

          const imageElement = parsed.querySelector('meta[property^="og:image"]');
          let image = imageElement && imageElement.getAttribute('content');

          // Resize image to 240x240
          if (image) {
            if (/_\d{2,4}x\d{2,4}/.test(image)) {
              image = image.replace(/_\d{2,4}x\d{2,4}/, '_240x240');
            } else {
              image = image.replace(/(\.(jpg|jpeg|png|webp|gif))(\?.*)?$/, '_240x240$1');
            }
          }

          snippet.insertAdjacentHTML(
            'afterend',
            `
            <a class="blogcard" href="${url}">
              ${
                image
                  ? `<figure class="blogcard__image"><img src="${image}" alt="${title}" style="width:100%;height:100%;object-fit:cover;"></figure>`
                  : ''
              }
              <div class="blogcard__content">
                <div class="blogcard__title">${title}</div>
                <div class="blogcard__description">${description}</div>
                <div class="blogcard__url">${url}</div>
              </div>
            </a>
          `
          );
        } catch (error) {
          console.error('Blog card generation error:', error);
        } finally {
          snippet.parentNode.removeChild(snippet);
        }
        return;
      }

      // Handle product URLs
      if (url.includes('products')) {
        try {
          const response = await fetch(url);
          const rawHTML = await response.text();
          const parsed = domParser.parseFromString(rawHTML, 'text/html');

          let mainPrice, subPrice;

          const priceElements = [
            ...parsed.querySelectorAll('.selling-plan-select__price i'),
          ];

          if (priceElements.length > 0) {
            mainPrice =
              priceElements[0] &&
              priceElements[0].textContent.replace(/(\(税込|\))/g, '');
            subPrice =
              priceElements[1] &&
              priceElements[1].textContent.replace(/(\(税込|\))/g, '');
          } else {
            const priceElement = parsed.querySelector('.product__price');
            mainPrice = priceElement && priceElement.textContent;
          }

          const titleElement = parsed.querySelector('meta[property="og:title"]');
          const title = titleElement && titleElement.getAttribute('content');

          const descriptionElement = parsed.querySelector(
            'meta[property="og:description"]'
          );
          const description =
            descriptionElement && descriptionElement.getAttribute('content');

          const imageElement = parsed.querySelector('meta[property^="og:image"]');
          const image = imageElement && imageElement.getAttribute('content');

          const buttonText = (() => {
            const regex2 = new RegExp(`^${window.location.origin}/`);
            const sameDomain = regex2.test(url);
            if (!sameDomain) return 'リンク先はこちら';
            const splitted = url.replace(regex2, '').split('/');
            const category = splitted[0];
            switch (category) {
              case 'products':
                return '商品を見る';
              case 'collection':
                return '商品一覧を見る';
              case 'blogs':
                return splitted.length > 2 ? '記事を見る' : '記事一覧を見る';
              default:
                return 'リンク先はこちら';
            }
          })();

          snippet.insertAdjacentHTML(
            'afterend',
            `
            <a class="blogcard" href="${url}">
              ${
                image
                  ? `
              <figure class="blogcard__image">
                <img src="${image}" alt="${title}">
              </figure>
            `
                  : ''
              }
              <div class="blogcard__content">
                <div class="blogcard__title">${title}</div>
                ${
                  mainPrice || subPrice
                    ? `<div class="blogcard__price">`
                    : ''
                }
                  ${subPrice ? `<span class="strike">${subPrice}</span>` : ''}
                  ${mainPrice ? `<span class="bold">${mainPrice}</span>` : ''}
                ${mainPrice || subPrice ? `</div>` : ''}
                <div class="blogcard__description">${description}</div>
              </div>
            </a>
          `
          );
        } catch (error) {
          console.error('Product card generation error:', error);
        } finally {
          snippet.parentNode.removeChild(snippet);
        }
        return;
      }

      // Other URLs - do nothing
      return;
    };

    Promise.all(targets.map((el) => replaceParagraphToProductLink(el)));
  }

  /**
   * Generate table of contents from H2-H4 headings (nested by level)
   * h1 は記事タイトルなので対象外。h2 を第1階層、h3・h4 を入れ子にする。
   */
  {
    const titles = [...origin.querySelectorAll('h2, h3, h4')].filter(
      (el) => el.innerText.trim() !== ''
    );
    // 見出しが1つだけの記事に目次を出しても、行き先が1つしかなく意味がない。
    // 2つ以上ある記事にだけ出す。
    if (titles.length < 2) return;

    // 本文が自分で id を持っている見出しは上書きしない。
    // 記事内ジャンプ（.ak-jump）のリンク先がその id を指しているので、
    // toc-N で潰すとジャンプが無反応になる。
    titles.forEach((el, index) => {
      el.id = el.id || `toc-${index + 1}`;
    });

    // 最上位の階層 = 記事にある最も浅い見出し（h2 が無い記事は h3 が第1階層になる）。
    const topLevel = Math.min(...titles.map((el) => Number(el.tagName[1])));
    const escapeHtml = (t) =>
      t.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

    // 見出しの並びから入れ子の <ul> を組む。h2 の次に h4 が来るような飛びは1段だけ下げる。
    let listHtml = '';
    let depth = 0;
    titles.forEach((el, index) => {
      const level = Number(el.tagName[1]) - topLevel;
      const target = index === 0 ? 0 : Math.min(level, depth + 1);
      if (target > depth) {
        listHtml += '<ul class="toc__list toc__list--sub">';
      } else {
        listHtml += '</li>';
        for (let d = depth; d > target; d--) listHtml += '</ul></li>';
      }
      depth = target;
      const text = escapeHtml(el.innerText.trim());
      listHtml += `<li class="toc__list-item toc__list-item--h${
        target + 2
      }"><a href="#${el.id}" data-consider-header="true">${text}</a>`;
    });
    listHtml += '</li>';
    for (let d = depth; d > 0; d--) listHtml += '</ul></li>';

    const tocHtml = `
    <div class="toc">
      <p class="toc__title"><span>目次</span></p>
      <ul class="toc__list">${listHtml}</ul>
    </div>
    `;

    // 移行記事は本文に自前の <style> を持ち、`#wrapper div { margin: 0; padding: 0 }` のような
    // ID セレクタで目次の余白を潰してしまう。第一見出しの直前ではなく、本文直下の
    // トップレベル要素の手前に差し込むことで、記事側のスコープ付き CSS の外に置く。
    const contentRoot = origin.querySelector('rte-formatter') || origin;
    let anchor = titles[0];
    while (anchor.parentElement && anchor.parentElement !== contentRoot) {
      anchor = anchor.parentElement;
    }

    // 記事の hide_toc が true のときは目次を出さない。
    // 見出しへの id 付与（記事内ジャンプの行き先）は上で済ませてあるので、差し込みだけを止める。
    if (!origin.hasAttribute('data-hide-toc')) {
      anchor.insertAdjacentHTML('beforebegin', tocHtml);
    }
  }
})();
